import { ButtonBase } from '@mui/material';
import FeedbackRevertButton from 'components/Grading/FeedbackRevertButton';
import FeedbackRevertPreview from 'components/Grading/FeedbackRevertPreview';
import SupportingEvidence from 'components/Grading/SupportingEvidence';
import { useDialog } from 'contexts/dialog';
import { useJoyride } from 'contexts/joyride';
import { useSnackbar } from 'contexts/snackbar';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Divider,
    IconButton,
    Loading,
    Paper,
    RichTextArea,
    Stack,
    styled,
    Tag,
    Text,
    Tooltip,
} from 'shell/components';
import {
    COMMENTARY_ITEM_ID,
    getDetailedFeedbackRevertPreview,
    getFeedbackRevertPreview,
} from 'utils/feedbackRegeneration';
import { getTemplatedFeedback, getTooltipComponent, stripHTML } from 'utils/helper';

const FeedbackRenderer = memo(
    ({
        assignment,
        currentSubmission,
        selectedFileId,
        setSelectedFileId,
        locations,
        form,
        setForm,
        selectedFeedbackId,
        setSelectedFeedbackId,
        pendingFeedbackItemIds = [],
        feedbackRegenerationNeededItemIds = [],
        feedbackUpdatedItemIds = [],
        onRevertFeedback,
        handleCriterionNavigation,
    }) => {
        const { setDialog, closeDialog } = useDialog();
        const { joyrideRun: isTourRunning } = useJoyride();
        const joyrideRun = isTourRunning && assignment?.type !== 'quiz';
        const { message } = useSnackbar();
        const { t } = useTranslation('assignment');
        const [revertPreview, setRevertPreview] = useState(null);

        const isFeedbackRubric = currentSubmission.ai.feedback.feedback_type === 'rubric';
        const isFeedbackTemplated = currentSubmission.ai.feedback.feedback_type === 'templated';
        const pendingFeedbackItemIdSet = useMemo(() => new Set(pendingFeedbackItemIds), [pendingFeedbackItemIds]);
        const feedbackRegenerationNeededItemIdSet = useMemo(
            () => new Set(feedbackRegenerationNeededItemIds),
            [feedbackRegenerationNeededItemIds]
        );
        const feedbackUpdatedItemIdSet = useMemo(() => new Set(feedbackUpdatedItemIds), [feedbackUpdatedItemIds]);
        const activeDetailedFeedback = form.feedback.improvement_suggestions?.filter(({ active }) => active) ?? [];
        const hasDetailedFeedbackRegenerationSuggestion =
            !isFeedbackRubric &&
            activeDetailedFeedback.some(
                ({ active, id }) =>
                    active && feedbackRegenerationNeededItemIdSet.has(id) && !pendingFeedbackItemIdSet.has(id)
            );
        const hasPendingDetailedFeedback = form.feedback.improvement_suggestions?.some(({ id }) =>
            pendingFeedbackItemIdSet.has(id)
        );
        const hasDetailedFeedbackUpdated =
            !isFeedbackRubric &&
            !hasDetailedFeedbackRegenerationSuggestion &&
            !hasPendingDetailedFeedback &&
            activeDetailedFeedback.some(({ id }) => feedbackUpdatedItemIdSet.has(id));
        const canRevertFeedbackItems = (itemIds) =>
            itemIds.length === 1 && Boolean(getFeedbackRevertPreview(currentSubmission.feedback_history, itemIds[0]));
        const commentaryRevertPreview = revertPreview?.itemId === COMMENTARY_ITEM_ID ? revertPreview : null;
        const availableDetailedRevertPreview = useMemo(
            () =>
                isFeedbackRubric
                    ? null
                    : getDetailedFeedbackRevertPreview(
                          currentSubmission.feedback_history,
                          form.feedback.improvement_suggestions
                      ),
            [currentSubmission.feedback_history, form.feedback.improvement_suggestions, isFeedbackRubric]
        );
        const detailedRevertPreview = revertPreview?.itemIds ? revertPreview : null;

        const handleOpenRevertPreview = useCallback(
            (itemIds) => {
                const itemId = itemIds[0];
                const preview = getFeedbackRevertPreview(currentSubmission.feedback_history, itemId);
                if (!preview) return;

                setRevertPreview(preview);
                if (itemId !== COMMENTARY_ITEM_ID) setSelectedFeedbackId(itemId);
            },
            [currentSubmission.feedback_history, setSelectedFeedbackId]
        );

        const handleCancelRevertPreview = useCallback(() => setRevertPreview(null), []);

        const handleConfirmRevertPreview = useCallback(async () => {
            if (!revertPreview || typeof onRevertFeedback !== 'function') return false;

            const didRevert = await onRevertFeedback({
                historyId: revertPreview.historyId,
                itemIds: revertPreview.itemIds ?? [revertPreview.itemId],
            });
            if (didRevert) setRevertPreview(null);
            return didRevert;
        }, [onRevertFeedback, revertPreview]);

        useEffect(() => setRevertPreview(null), [currentSubmission.id]);

        useEffect(() => {
            if (!revertPreview) return;
            const latestPreview = revertPreview.itemIds
                ? availableDetailedRevertPreview
                : getFeedbackRevertPreview(currentSubmission.feedback_history, revertPreview.itemId);
            if (latestPreview?.historyId !== revertPreview.historyId) setRevertPreview(null);
        }, [availableDetailedRevertPreview, currentSubmission.feedback_history, revertPreview]);

        const locationData = useMemo(() => {
            return {
                locations,
                files: currentSubmission?.files ?? [],
            };
        }, [locations, currentSubmission?.files]);

        const handleClickCopy = async (string) => {
            await navigator.clipboard.writeText(stripHTML(string));
            message.success(t('common:message.copied-to-clipboard', 'Copied to clipboard.'));
        };

        const handleClickDelete = () => {
            setDialog({
                open: true,
                message: t('dialog.delete-feedback.title', 'Delete Feedback?'),
                description: t(
                    'dialog.delete-feedback.description',
                    'Are you sure you want to delete this feedback? This can’t be undone.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.delete', 'Delete'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    setForm((current) => ({ ...current, feedback: { ...current.feedback, commentary: '' } }));
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        };

        const handleClickCopyAll = async () => {
            const noFeedbackTranslationArgs = assignment?.api_assignment_id
                ? [
                      'feedback-section.no-feedback-no-pushback',
                      'No feedback provided. This message will not be passed back.',
                  ]
                : ['feedback-section.no-feedback', 'No feedback provided.'];

            const str = form.feedback.improvement_suggestions
                .filter(({ active }) => active)
                .reduce((acc, cur, index) => {
                    if (isFeedbackRubric) {
                        const { criterion_id, positive, negative } = cur;
                        let feedback = '';
                        if (index) feedback += '\n\n';
                        const criterion = assignment.rubric.criteria.find(({ id }) => id === criterion_id);
                        feedback += `${criterion.title}\n\n`;
                        feedback += `${t('feedback-section.positive-title', 'What you did well')}\n\n`;
                        feedback +=
                            !positive.active && positive.text
                                ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                                : positive.text === ''
                                  ? t(...noFeedbackTranslationArgs)
                                  : stripHTML(positive.text);
                        feedback += `\n\n${t('feedback-section.improvement-title', 'How you can improve')}\n\n`;
                        feedback +=
                            !negative.active && negative.text
                                ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                                : negative.text === ''
                                  ? t(...noFeedbackTranslationArgs)
                                  : stripHTML(negative.text);
                        return `${acc}${feedback}`;
                    }
                    return !index ? `${acc}${stripHTML(cur.text)}` : `${acc}\n\n${stripHTML(cur.text)}`;
                }, '');
            await navigator.clipboard.writeText(str);
            message.success(t('common:message.copied-to-clipboard', 'Copied to clipboard.'));
        };

        const handleClickDeleteAll = async () => {
            setDialog({
                open: true,
                message: t('dialog.delete-all-feedback.title', 'Delete All Feedback?'),
                description: t(
                    'dialog.delete-all-feedback.description',
                    'Are you sure you want to delete all feedback? This can’t be undone.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.delete', 'Delete'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    setForm((current) => ({
                        ...current,
                        feedback: {
                            ...current.feedback,
                            improvement_suggestions: current.feedback.improvement_suggestions.map((item) =>
                                isFeedbackRubric
                                    ? {
                                          ...item,
                                          positive: {
                                              ...item.positive,
                                              active: false,
                                          },
                                          negative: {
                                              ...item.negative,
                                              active: false,
                                          },
                                      }
                                    : {
                                          ...item,
                                          active: false,
                                      }
                            ),
                        },
                    }));
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        };

        if (!form.feedback) return null;

        if (isFeedbackTemplated) {
            return (
                <>
                    <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                            <Text variant={'p1'} weight={'semibold'}>
                                {assignment.type === 'quiz'
                                    ? t('quiz:grading.feedback-label', 'Quiz Feedback')
                                    : t('feedback-section.assignment-feedback-label', 'Assignment Feedback')}
                            </Text>
                            <Tooltip
                                content={
                                    assignment.type === 'quiz'
                                        ? t(
                                              'quiz:grading.templated-feedback-tooltip',
                                              'This quiz is using Smart Feedback Templates. When you make a grading selection, the feedback template for that grade will appear here.'
                                          )
                                        : t(
                                              'feedback-section.templated-feedback-tooltip',
                                              'This assignment is using Smart Feedback Templates. When you make a grading selection, the feedback template for that grade will appear here.'
                                          )
                                }
                                type={'info'}
                            />
                        </Stack>
                        <IconButton
                            aria-label={'copy feedback'}
                            disabled={!Object.keys(form.criteria).length}
                            kind={'copy'}
                            size={'small'}
                            tooltip={{ component: t('common:controls.copy', 'Copy') }}
                            variant={'square'}
                            onClick={() => handleClickCopy(getTemplatedFeedback(assignment, form))}
                        />
                    </BackgroundStack>
                    <Stack sx={{ flex: 1, position: 'relative', p: 3 }}>
                        {Object.keys(form.criteria).length ? (
                            <Text rich>{getTemplatedFeedback(assignment, form)}</Text>
                        ) : (
                            <CenterText align={'center'} color={'light'}>
                                {assignment.type === 'quiz'
                                    ? t(
                                          'quiz:grading.templated-feedback-description',
                                          'This quiz is using Smart Feedback Templates. Feedback will appear here as you make grading selections.'
                                      )
                                    : t(
                                          'feedback-section.templated-feedback-description',
                                          'This assignment is using Smart Feedback Templates. Feedback will appear here as you make grading selections.'
                                      )}
                            </CenterText>
                        )}
                    </Stack>
                </>
            );
        }

        return (
            <>
                <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text variant={'p1'} weight={'semibold'}>
                            {t('feedback-section.general-feedback-label', 'General Feedback')}
                        </Text>
                        {!isFeedbackRubric && (
                            <Tooltip
                                content={getTooltipComponent(
                                    t('feedback-section.general-feedback-label', 'General Feedback'),
                                    t(
                                        'feedback-section.system-feedback-tooltip',
                                        'This section provides a broad overview, highlighting key strengths and areas for improvement across student work.'
                                    )
                                )}
                                type={'info'}
                            />
                        )}
                    </Stack>
                    <Stack direction={'row'} spacing={2}>
                        <IconButton
                            aria-label={'copy feedback'}
                            disabled={
                                !form.feedback.commentary ||
                                pendingFeedbackItemIdSet.has(COMMENTARY_ITEM_ID) ||
                                Boolean(commentaryRevertPreview)
                            }
                            kind={'copy'}
                            size={'small'}
                            tooltip={{ component: t('common:controls.copy', 'Copy') }}
                            variant={'square'}
                            onClick={() => handleClickCopy(form.feedback.commentary)}
                        />
                        <IconButton
                            aria-label={'delete feedback'}
                            disabled={
                                !form.feedback.commentary ||
                                joyrideRun ||
                                pendingFeedbackItemIdSet.has(COMMENTARY_ITEM_ID) ||
                                Boolean(commentaryRevertPreview)
                            }
                            kind={'delete'}
                            size={'small'}
                            tooltip={{ component: t('common:controls.delete', 'Delete') }}
                            variant={'square'}
                            onClick={handleClickDelete}
                        />
                    </Stack>
                </BackgroundStack>
                <Stack sx={{ p: 3 }} spacing={2}>
                    <FeedbackItemHeader
                        canRevert={canRevertFeedbackItems([COMMENTARY_ITEM_ID])}
                        itemIds={[COMMENTARY_ITEM_ID]}
                        needsRegeneration={feedbackRegenerationNeededItemIdSet.has(COMMENTARY_ITEM_ID)}
                        pending={pendingFeedbackItemIdSet.has(COMMENTARY_ITEM_ID)}
                        revertPreviewOpen={Boolean(commentaryRevertPreview)}
                        updated={feedbackUpdatedItemIdSet.has(COMMENTARY_ITEM_ID)}
                        onRevertFeedback={handleOpenRevertPreview}
                    />
                    {commentaryRevertPreview && (
                        <FeedbackRevertPreview
                            feedback={commentaryRevertPreview.feedback}
                            feedbackType={'commentary'}
                            generatedOn={commentaryRevertPreview.generatedOn}
                            onCancel={handleCancelRevertPreview}
                            onRevert={handleConfirmRevertPreview}
                        />
                    )}
                    {pendingFeedbackItemIdSet.has(COMMENTARY_ITEM_ID) ? (
                        <Loading sx={{ height: '10vh' }} />
                    ) : (
                        <CustomRichTextArea
                            sx={{ m: -1 }}
                            placeholder={t('feedback-section.feedback-placeholder', 'Write your feedback here')}
                            readOnly={Boolean(commentaryRevertPreview)}
                            value={form.feedback.commentary}
                            variant={'dynamic'}
                            onChange={(html) =>
                                setForm((current) => ({
                                    ...current,
                                    feedback: { ...current.feedback, commentary: html },
                                }))
                            }
                        />
                    )}
                </Stack>
                {Boolean(form.feedback.improvement_suggestions?.length) && (
                    <>
                        <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text variant={'p1'} weight={'semibold'}>
                                    {currentSubmission.ai.feedback.feedback_type === 'rubric'
                                        ? t('feedback-section.assignment-feedback-label', 'Assignment Feedback')
                                        : t('feedback-section.detailed-feedback-label', 'Detailed Feedback')}
                                </Text>
                                {!isFeedbackRubric && (
                                    <Tooltip
                                        content={getTooltipComponent(
                                            t('feedback-section.detailed-feedback-label', 'Detailed Feedback'),
                                            t(
                                                'feedback-section.detailed-feedback-tooltip',
                                                'Here, specific feedback is provided with highlighted examples. Students can use these examples to help them identify other areas where they can improve.'
                                            )
                                        )}
                                        type={'info'}
                                    />
                                )}
                            </Stack>
                            <Stack direction={'row'} spacing={2}>
                                <IconButton
                                    aria-label={'copy feedback'}
                                    disabled={
                                        Boolean(detailedRevertPreview) ||
                                        (isFeedbackRubric
                                            ? form.feedback.improvement_suggestions.every(
                                                  ({ positive, negative }) => !positive.action && !negative.active
                                              )
                                            : form.feedback.improvement_suggestions.every(({ active }) => !active))
                                    }
                                    kind={'copy'}
                                    size={'small'}
                                    tooltip={{ component: t('common:controls.copy', 'Copy') }}
                                    variant={'square'}
                                    onClick={handleClickCopyAll}
                                />
                                <IconButton
                                    aria-label={'delete feedback'}
                                    disabled={
                                        joyrideRun ||
                                        Boolean(detailedRevertPreview) ||
                                        (isFeedbackRubric
                                            ? form.feedback.improvement_suggestions.every(
                                                  ({ positive, negative }) => !positive.action && !negative.active
                                              )
                                            : form.feedback.improvement_suggestions.every(({ active }) => !active))
                                    }
                                    kind={'delete'}
                                    size={'small'}
                                    tooltip={{ component: t('common:controls.delete', 'Delete') }}
                                    variant={'square'}
                                    onClick={handleClickDeleteAll}
                                />
                            </Stack>
                        </BackgroundStack>
                        <Stack sx={{ position: 'relative', flex: 1, p: 3 }} spacing={2.5}>
                            {!isFeedbackRubric && (
                                <FeedbackItemHeader
                                    canRevert={Boolean(availableDetailedRevertPreview)}
                                    itemIds={availableDetailedRevertPreview?.itemIds ?? []}
                                    needsRegeneration={hasDetailedFeedbackRegenerationSuggestion}
                                    pending={hasPendingDetailedFeedback}
                                    revertPreviewOpen={Boolean(detailedRevertPreview)}
                                    updated={hasDetailedFeedbackUpdated}
                                    onRevertFeedback={
                                        typeof onRevertFeedback === 'function'
                                            ? () => setRevertPreview(availableDetailedRevertPreview)
                                            : undefined
                                    }
                                />
                            )}
                            {detailedRevertPreview && (
                                <FeedbackRevertPreview
                                    feedback={detailedRevertPreview.feedback}
                                    feedbackType={'detailed'}
                                    generatedOn={detailedRevertPreview.generatedOn}
                                    onCancel={handleCancelRevertPreview}
                                    onRevert={handleConfirmRevertPreview}
                                />
                            )}
                            {!form.feedback.improvement_suggestions.filter(({ active }) => active).length ? (
                                <Text
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                    }}
                                    color={'light'}
                                >
                                    {t('feedback-section.deleted-feedback', 'This feedback was deleted.')}
                                </Text>
                            ) : (
                                form.feedback.improvement_suggestions
                                    .filter(({ active }) => active)
                                    .map((item) =>
                                        isFeedbackRubric ? (
                                            <CriterionFeedback
                                                key={item.id}
                                                assignment={assignment}
                                                feedback={item}
                                                setForm={setForm}
                                                selectedFeedbackId={selectedFeedbackId}
                                                setSelectedFeedbackId={setSelectedFeedbackId}
                                                handleCriterionNavigation={handleCriterionNavigation}
                                                locationData={locationData}
                                                selectedFileId={selectedFileId}
                                                setSelectedFileId={setSelectedFileId}
                                                pending={pendingFeedbackItemIdSet.has(item.id)}
                                                needsRegeneration={feedbackRegenerationNeededItemIdSet.has(item.id)}
                                                updated={feedbackUpdatedItemIdSet.has(item.id)}
                                                canRevert={canRevertFeedbackItems([item.id])}
                                                revertPreview={revertPreview?.itemId === item.id ? revertPreview : null}
                                                onCancelRevertPreview={handleCancelRevertPreview}
                                                onConfirmRevertPreview={handleConfirmRevertPreview}
                                                onRevertFeedback={handleOpenRevertPreview}
                                            />
                                        ) : (
                                            <DetailedFeedback
                                                key={item.id}
                                                feedback={item}
                                                setForm={setForm}
                                                selectedFeedbackId={selectedFeedbackId}
                                                setSelectedFeedbackId={setSelectedFeedbackId}
                                                pending={pendingFeedbackItemIdSet.has(item.id)}
                                                readOnly={Boolean(detailedRevertPreview)}
                                            />
                                        )
                                    )
                            )}
                        </Stack>
                    </>
                )}
            </>
        );
    }
);

const FeedbackRegenerationStatus = ({ needsRegeneration, updated }) => {
    if (needsRegeneration) return <Tag type={'general'} value={'regenerationSuggested'} />;
    if (updated) return <Tag type={'general'} value={'feedbackUpdated'} />;
    return null;
};

const FeedbackItemHeader = ({
    canRevert,
    itemIds,
    needsRegeneration,
    pending,
    revertPreviewOpen,
    updated,
    onRevertFeedback,
}) => {
    const hasStatus = !pending && (needsRegeneration || updated);
    const hasRevert = canRevert && typeof onRevertFeedback === 'function';
    if (!hasStatus && !hasRevert) return null;

    return (
        <Stack sx={{ width: '100%' }} alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
            {hasStatus && <FeedbackRegenerationStatus needsRegeneration={needsRegeneration} updated={updated} />}
            <FeedbackRevertButton
                canRevert={canRevert}
                itemIds={itemIds}
                pending={pending || revertPreviewOpen}
                onRevertFeedback={onRevertFeedback}
            />
        </Stack>
    );
};

const CriterionFeedback = memo(
    ({
        assignment,
        feedback,
        setForm,
        selectedFeedbackId,
        setSelectedFeedbackId,
        handleCriterionNavigation,
        locationData,
        selectedFileId,
        setSelectedFileId,
        pending,
        needsRegeneration,
        updated,
        canRevert,
        revertPreview,
        onCancelRevertPreview,
        onConfirmRevertPreview,
        onRevertFeedback,
    }) => {
        const { setDialog, closeDialog } = useDialog();
        const { message } = useSnackbar();
        const { t } = useTranslation('assignment');

        const [isHovered, setIsHovered] = useState(false);
        const hasFeedbackItemHeader =
            (canRevert && typeof onRevertFeedback === 'function') || (!pending && (needsRegeneration || updated));

        const { locations, files } = locationData;

        const noFeedbackTranslationArgs = assignment?.api_assignment_id
            ? [
                  'feedback-section.no-feedback-no-pushback',
                  'No feedback provided. This message will not be passed back.',
              ]
            : ['feedback-section.no-feedback', 'No feedback provided.'];

        const updateForm = (feedbackType, value) => {
            setForm((current) => {
                return {
                    ...current,
                    feedback: {
                        ...current.feedback,
                        improvement_suggestions: current.feedback.improvement_suggestions.map((item) => {
                            return item.id === feedback.id
                                ? {
                                      ...item,
                                      [feedbackType]: {
                                          ...item[feedbackType],
                                          active: true,
                                          text: value,
                                      },
                                  }
                                : item;
                        }),
                    },
                };
            });
        };

        const onClickCopy = async (e) => {
            e.stopPropagation();
            let accFeedback = '';
            accFeedback += `${t('feedback-section.positive-title', 'What you did well')}\n\n`;
            accFeedback += !feedback.positive.active
                ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                : feedback.positive.text === ''
                  ? t(...noFeedbackTranslationArgs)
                  : stripHTML(feedback.positive.text);
            accFeedback += `\n\n${t('feedback-section.improvement-title', 'How you can improve')}\n\n`;
            accFeedback += !feedback.negative.active
                ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                : feedback.negative.text === ''
                  ? t(...noFeedbackTranslationArgs)
                  : stripHTML(feedback.negative.text);
            await navigator.clipboard.writeText(accFeedback);
            message.success(t('common:message.copied-to-clipboard', 'Copied to clipboard.'));
        };

        const onClickDelete = (e) => {
            e.stopPropagation();
            setDialog({
                open: true,
                message: t('dialog.delete-feedback.title', 'Delete Feedback?'),
                description: t(
                    'dialog.delete-feedback.description',
                    'Are you sure you want to delete this feedback? This can’t be undone.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.delete', 'Delete'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    setForm((current) => {
                        return {
                            ...current,
                            feedback: {
                                ...current.feedback,
                                improvement_suggestions: current.feedback.improvement_suggestions.map((item) => {
                                    return item.id === feedback.id
                                        ? {
                                              ...item,
                                              positive: {
                                                  ...item['positive'],
                                                  active: false,
                                              },
                                              negative: {
                                                  ...item['negative'],
                                                  active: false,
                                              },
                                          }
                                        : item;
                                }),
                            },
                        };
                    });
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        };

        return (
            <CustomButtonBase
                id={feedback.id}
                component={'div'}
                role={'presentation'}
                onClick={() =>
                    setSelectedFeedbackId(
                        Boolean(revertPreview) || selectedFeedbackId !== feedback.id ? feedback.id : null
                    )
                }
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <CustomPaper
                    isActive={Boolean(revertPreview) || selectedFeedbackId === feedback.id}
                    variant={'outlined'}
                >
                    <Stack>
                        <FeedbackItemHeader
                            canRevert={canRevert}
                            itemIds={[feedback.id]}
                            needsRegeneration={needsRegeneration}
                            pending={pending}
                            revertPreviewOpen={Boolean(revertPreview)}
                            updated={updated}
                            onRevertFeedback={onRevertFeedback}
                        />
                        <Text sx={hasFeedbackItemHeader ? { mt: 2 } : undefined} variant={'sh2'} weight={'semibold'}>
                            {assignment.rubric.criteria.find(({ id }) => id === feedback.criterion_id).title}
                        </Text>
                        <Divider sx={{ mt: 1.5, mb: 2 }} />
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                            <Button
                                sx={{ width: 'fit-content' }}
                                disabled={pending || Boolean(revertPreview)}
                                variant={'secondary'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCriterionNavigation(feedback.criterion_id);
                                }}
                            >
                                {t('rubric.view-rubric', 'View Rubric')}
                            </Button>
                            <Stack direction={'row'} spacing={2}>
                                <IconButton
                                    disabled={pending || Boolean(revertPreview)}
                                    kind={'copy'}
                                    size={'small'}
                                    tooltip={{ component: t('common:controls.copy', 'Copy') }}
                                    variant={'square'}
                                    onClick={onClickCopy}
                                />
                                <IconButton
                                    disabled={pending || Boolean(revertPreview)}
                                    kind={'delete'}
                                    size={'small'}
                                    tooltip={{ component: t('common:controls.delete', 'Delete') }}
                                    variant={'square'}
                                    onClick={onClickDelete}
                                />
                            </Stack>
                        </Stack>
                        {revertPreview && (
                            <Stack sx={{ mt: 2 }}>
                                <FeedbackRevertPreview
                                    feedback={revertPreview.feedback}
                                    feedbackType={'rubric'}
                                    generatedOn={revertPreview.generatedOn}
                                    onCancel={onCancelRevertPreview}
                                    onRevert={onConfirmRevertPreview}
                                />
                            </Stack>
                        )}
                        <Stack sx={revertPreview ? { mt: 2 } : undefined} spacing={3}>
                            <Stack sx={{ p: 1, pt: 2 }} spacing={2}>
                                <Text sx={{ p: 1 }} variant={'p1'}>
                                    {t('feedback-section.positive-title', 'What you did well')}
                                </Text>
                                {pending ? (
                                    <Loading sx={{ height: '10vh' }} />
                                ) : (
                                    <CustomRichTextArea
                                        sx={{ m: -1 }}
                                        placeholder={t(...noFeedbackTranslationArgs)}
                                        readOnly={Boolean(revertPreview)}
                                        variant={'dynamic'}
                                        value={feedback.positive.active ? feedback.positive.text : ''}
                                        onChange={(html) => updateForm('positive', html)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedFeedbackId(feedback.id);
                                        }}
                                    />
                                )}
                            </Stack>
                            <Stack
                                sx={(theme) => ({
                                    borderRadius: '8px',
                                    mx: -1,
                                    p: 2,
                                    background: theme.palette.ensightful.warning[200],
                                })}
                                spacing={2}
                            >
                                <Text sx={{ p: 1 }} variant={'p1'}>
                                    {t('feedback-section.improvement-title', 'How you can improve')}
                                </Text>
                                {pending ? (
                                    <Loading sx={{ height: '10vh' }} />
                                ) : (
                                    <CustomRichTextArea
                                        sx={{ m: -1 }}
                                        placeholder={t(...noFeedbackTranslationArgs)}
                                        readOnly={Boolean(revertPreview)}
                                        value={feedback.negative.active ? feedback.negative.text : ''}
                                        variant={'dynamic'}
                                        onChange={(html) => updateForm('negative', html)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedFeedbackId(feedback.id);
                                        }}
                                    />
                                )}
                            </Stack>
                            {selectedFeedbackId === feedback.id && (
                                <Box sx={{ m: -3 }}>
                                    <SupportingEvidence
                                        locations={locations}
                                        files={files}
                                        selectedFileId={selectedFileId}
                                        setSelectedFileId={setSelectedFileId}
                                    />
                                </Box>
                            )}
                        </Stack>
                    </Stack>
                    {selectedFeedbackId !== feedback.id && isHovered && <LeftBorderHighlight />}
                </CustomPaper>
            </CustomButtonBase>
        );
    },
    (prevProps, nextProps) => {
        return (
            prevProps.feedback.positive.active === nextProps.feedback.positive.active &&
            prevProps.feedback.positive.text === nextProps.feedback.positive.text &&
            prevProps.feedback.negative.active === nextProps.feedback.negative.active &&
            prevProps.feedback.negative.text === nextProps.feedback.negative.text &&
            prevProps.pending === nextProps.pending &&
            prevProps.needsRegeneration === nextProps.needsRegeneration &&
            prevProps.updated === nextProps.updated &&
            prevProps.canRevert === nextProps.canRevert &&
            prevProps.revertPreview === nextProps.revertPreview &&
            prevProps.onCancelRevertPreview === nextProps.onCancelRevertPreview &&
            prevProps.onConfirmRevertPreview === nextProps.onConfirmRevertPreview &&
            prevProps.onRevertFeedback === nextProps.onRevertFeedback &&
            prevProps.selectedFeedbackId === nextProps.selectedFeedbackId
        );
    }
);

const DetailedFeedback = ({ feedback, setForm, selectedFeedbackId, setSelectedFeedbackId, pending, readOnly }) => {
    const { t } = useTranslation('assignment');

    const [isHovered, setIsHovered] = useState(false);

    const handleChange = (html) => {
        setForm((current) => ({
            ...current,
            feedback: {
                ...current.feedback,
                improvement_suggestions: current.feedback.improvement_suggestions.map((item) => {
                    return item.id === feedback.id
                        ? {
                              ...item,
                              text: html,
                          }
                        : item;
                }),
            },
        }));
    };

    return (
        <CustomButtonBase
            component={'div'}
            role={'presentation'}
            onClick={() => setSelectedFeedbackId(selectedFeedbackId !== feedback.id ? feedback.id : null)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <CustomPaper isActive={selectedFeedbackId === feedback.id} variant={'outlined'}>
                {pending ? (
                    <Loading sx={{ height: '10vh' }} />
                ) : (
                    <CustomRichTextArea
                        sx={{ m: -2 }}
                        placeholder={t('feedback-section.feedback-placeholder', 'Write your feedback here')}
                        readOnly={readOnly}
                        value={feedback.text}
                        variant={'dynamic'}
                        onChange={handleChange}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFeedbackId(feedback.id);
                        }}
                    />
                )}
                {selectedFeedbackId !== feedback.id && isHovered && <LeftBorderHighlight />}
            </CustomPaper>
        </CustomButtonBase>
    );
};

const BackgroundStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(2, 5),
        background: theme.palette.ensightful.primary[50],
        position: 'sticky',
        top: 0,
        zIndex: 1,
    };
});

const CenterText = styled(Text)(() => {
    return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '85%',
        transform: 'translate(-50%, -50%)',
    };
});

const CustomButtonBase = styled(ButtonBase)(({ theme }) => {
    return {
        scrollMarginTop: `calc(40px + ${theme.spacing(3)})`,
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const CustomPaper = styled(Paper)(({ theme, isActive }) => {
    return {
        position: 'relative',
        width: '100%',
        padding: theme.spacing(3),
        ...(isActive
            ? {
                  border: `2px solid ${theme.palette.ensightful.warning[700]}`,
              }
            : {
                  border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
                  padding: `calc(${theme.spacing(3)} + 1px)`,
              }),
    };
});

const LeftBorderHighlight = styled(Box)(({ theme }) => {
    return {
        position: 'absolute',
        top: '50%',
        left: '-1px',
        height: 'calc(100% - 28px)',
        width: '2px',
        background: theme.palette.ensightful.warning[700],
        transform: 'translateY(-50%)',
    };
});

const CustomRichTextArea = styled(RichTextArea)(() => {
    return {
        '& > .MuiRichTextArea-root': {
            maxHeight: 'unset',
        },
    };
});

export default FeedbackRenderer;
